// ============================================================================
// LA CARTE « CE QUE LA CAISSE FAIT DU PORTEFEUILLE » — ce qu'elle montre, décidé hors du rendu.
//
// UTILITÉ : Intégrations → Wallet règle deux interrupteurs et le gabarit de coupon de chaque type de
// titre (maquette `Sliderz Backoffice.dc.html`, section `integIsWallet`). Tout ce que la carte AFFICHE
// — l'état de la tête, celui de chaque ligne, ce que le menu offre — se décide ici, en fonctions pures,
// pour être éprouvé sans navigateur (ce paquet n'a pas d'environnement DOM).
//
// ⚠️ 🔴 LA CARTE NE DIT JAMAIS « PART » QUAND RIEN NE PEUT PARTIR. La maquette connaît deux causes
// (wallet éteint, aucun gabarit) ; la réalité en a trois de plus, et chacune se dit : la connexion
// n'est pas établie, le gabarit a disparu du compte Eldowallet, deux types partagent le même gabarit.
// ⚠️ MODULE SANS REACT NI API : le registre des intégrations le lit pour déclarer les réglages du
// connecteur, et ne doit pas charger un écran avec lui.
// ============================================================================
import {
  COUPON_TITLE_TYPES, couponTemplateKey, distinctSettingHolders, switchOn,
  type CouponTitleType, type SwitchName,
} from '@orgazone/sync-contract';
import type { ReglageVue } from './screens/parametres-modele';
import { servedListView, type ServedList, type ServedOption } from './screens/served-lists';
import { key, type Translatable } from './i18n';

/** Un interrupteur de la carte — son libellé et son effet, relevés de la maquette. */
export interface WalletSwitchView {
  name: Extract<SwitchName, 'wallet.enabled' | 'wallet.auto'>;
  label: Translatable;
  hint: Translatable;
}

export const WALLET_SWITCHES: readonly WalletSwitchView[] = [
  { name: 'wallet.enabled', label: key('Wallet activé en caisse'), hint: key('Cartes et coupons peuvent partir vers Apple / Google Wallet.') },
  // ⚠️ L'AIDE DIT CE QUE FAIT LA CAISSE, pas le texte de la maquette (« la caisse demande le consentement ») :
  //    la fiche créée en caisse naît COCHÉE (`client-card.tsx`), aucune question n'est posée.
  { name: 'wallet.auto', label: key('Proposé aux nouveaux clients'), hint: key('Une fiche créée en caisse naît avec le wallet coché ; le vendeur peut le décocher.') },
];

/** Le libellé de chaque type de titre, dans l'ordre du contrat. */
export const COUPON_TYPE_LABELS: Readonly<Record<CouponTitleType, Translatable>> = {
  cadeau: key('Chèque cadeau'),
  fidelite: key('Bon de fidélité'),
  avoir: key('Avoir'),
  acompte: key('Acompte'),
};

/**
 * TOUT CE QUE LE CONNECTEUR WALLET RÈGLE — déclaré au registre des intégrations, écarté de Paramètres.
 * ⚠️ DÉRIVÉ des deux tables ci-dessus : la carte et le registre ne peuvent pas diverger sur la liste
 *    des réglages, et un réglage déclaré sans ligne à l'écran serait invisible partout.
 */
export const WALLET_SETTINGS: ReglageVue[] = [
  ...WALLET_SWITCHES.map((s): ReglageVue => ({ name: s.name, label: s.label, forme: 'toggle' })),
  ...COUPON_TITLE_TYPES.map((type): ReglageVue => ({
    name: couponTemplateKey(type), label: COUPON_TYPE_LABELS[type], forme: 'liste', source: 'wallet_coupon_templates',
  })),
];

/**
 * LE MONOGRAMME D'UN TYPE — les initiales de son libellé TRADUIT (« Chèque cadeau » → CC,
 * « Bon de fidélité » → BF, « Avoir » → AV).
 * ⚠️ DÉRIVÉ, JAMAIS ÉCRIT : « BF » n'a aucun sens pour un compte qui lit « Loyalty voucher ». Les mots
 *    de deux lettres ou moins (« de », « of ») ne comptent pas ; un seul mot donne ses deux premières lettres.
 */
export function couponBadge(label: string): string {
  const words = label.split(/[\s'’-]+/u).filter((w) => [...w].length > 2);
  const letters = words.length >= 2 ? words.slice(0, 2).map((w) => [...w][0]!) : [...(words[0] ?? label)].slice(0, 2);
  return letters.join('').toLocaleUpperCase();
}

/**
 * CE QUE LA SONDE DIT DE LA CONNEXION, pour la carte.
 * · `connected` / `disconnected` : un VERDICT de la sonde (clé absente, refusée, illisible, chiffrement
 *   absent au serveur = déconnecté) ;
 * · `checking` : la sonde relit la connexion ; `unknown` : notre API n'a pas pu la lire.
 * ⚠️ 🔴 NE PAS SAVOIR N'EST NI « ÇA PART » NI « RIEN NE PART » : sans verdict, la carte ne compte aucun type
 *    parmi ceux qui partent et le dit. Compter l'inconnu comme connecté ferait passer la carte au vert le
 *    temps de la sonde : « Tester la connexion » sur une clé refusée afficherait « part » avant de rougir.
 */
export type WalletConnection = 'connected' | 'disconnected' | 'checking' | 'unknown';

export function walletConnection(
  phase: 'chargement' | 'lu' | 'echec',
  status?: 'connecte' | 'attention' | 'erreur' | 'a_venir' | 'lecture',
): WalletConnection {
  if (phase === 'chargement') return 'checking';
  if (phase === 'echec') return 'unknown';
  if (status === 'connecte') return 'connected';
  if (status === 'attention' || status === 'erreur') return 'disconnected';
  return 'unknown';
}

/** L'état de la tête de carte — ce qui part, pour tout le magasin. */
export type WalletHead =
  | { kind: 'off' }
  | { kind: 'disconnected' }
  | { kind: 'checking' }
  | { kind: 'unknown' }
  | { kind: 'none' }
  | { kind: 'some'; n: number; total: number };

/**
 * L'état d'une ligne de type de titre.
 * · `off` : le wallet est désactivé · `unset` : aucun gabarit, le type ne part pas
 * · `disconnected` : un gabarit est réglé, mais la connexion n'est pas établie
 * · `unverified` : un gabarit est réglé, et la connexion n'a pas de verdict
 * · `missing` : le gabarit réglé n'est plus sur le compte Eldowallet — l'envoi échouerait
 * · `shared` : il part, mais sous le gabarit d'un autre type · `sent` : il part
 */
export type WalletRowState = 'off' | 'unset' | 'disconnected' | 'unverified' | 'missing' | 'shared' | 'sent';

/** Un gabarit proposé dans le menu d'une ligne. */
export interface WalletTemplateOption extends ServedOption {
  /** Le type qui porte déjà ce gabarit — il est grisé ici, et nommé. */
  takenBy: CouponTitleType | null;
}

/** Ce que le menu d'une ligne peut offrir. */
export type WalletMenu =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'empty'; note: Translatable }
  | { kind: 'choices'; options: WalletTemplateOption[] };

export interface WalletTemplateRow {
  type: CouponTitleType;
  /** La clé de réglage — c'est elle qui voyage. */
  name: string;
  /** Le gabarit enregistré, sans espaces de bord — `''` = aucun. */
  value: string;
  /** Le gabarit enregistré, retrouvé dans la liste servie — `null` s'il n'y est pas (ou pas encore). */
  template: ServedOption | null;
  state: WalletRowState;
  /** Les AUTRES types qui portent le même gabarit. */
  sharedWith: CouponTitleType[];
  menu: WalletMenu;
}

export interface WalletCardView {
  enabled: boolean;
  auto: boolean;
  head: WalletHead;
  rows: WalletTemplateRow[];
}

const TYPE_OF_KEY = new Map<string, CouponTitleType>(COUPON_TITLE_TYPES.map((type) => [couponTemplateKey(type), type]));
const typesOf = (keys: readonly string[]): CouponTitleType[] =>
  keys.flatMap((k) => { const type = TYPE_OF_KEY.get(k); return type ? [type] : []; });

/**
 * CE QUE LA CARTE MONTRE, décidé d'un bloc.
 *
 * ⚠️ L'ORDRE DES ÉTATS D'UNE LIGNE COMPTE : ce qui empêche TOUT envoi l'emporte (wallet éteint, pas de
 * gabarit, pas de connexion), puis ce qui fait ÉCHOUER celui-ci (gabarit disparu), puis ce qui le rend
 * trompeur (gabarit partagé). Une liste pas encore lue ou refusée ne prouve pas l'absence du gabarit :
 * la ligne garde alors ce que le réglage décide, sans affirmer ce qu'elle ne sait pas.
 */
export function walletCardView(input: {
  /** Les valeurs enregistrées, par nom — `null` = jamais réglé. */
  values: Readonly<Record<string, string | null>>;
  /** Les gabarits servis par le fournisseur — `null` tant qu'ils ne sont pas lus. */
  templates: ServedList | null;
  connection: WalletConnection;
}): WalletCardView {
  const enabled = switchOn('wallet.enabled', input.values['wallet.enabled']);
  const auto = switchOn('wallet.auto', input.values['wallet.auto']);

  const rows = COUPON_TITLE_TYPES.map((type): WalletTemplateRow => {
    const name = couponTemplateKey(type);
    const value = (input.values[name] ?? '').trim();
    const listed = servedListView(input.templates, value, 'wallet_coupon_templates');
    const sharedWith = typesOf(distinctSettingHolders(input.values, name, value));
    const menu: WalletMenu = listed.kind === 'choices'
      ? {
          kind: 'choices',
          options: listed.options.map((o) => ({ ...o, takenBy: typesOf(distinctSettingHolders(input.values, name, o.id))[0] ?? null })),
        }
      : listed.kind === 'empty' ? { kind: 'empty', note: listed.note } : listed;
    const template = listed.kind === 'choices' ? listed.options.find((o) => o.id === value) ?? null : null;
    const missing = (listed.kind === 'choices' || listed.kind === 'empty') && listed.unknownValue !== null;

    const state: WalletRowState = !enabled ? 'off'
      : value === '' ? 'unset'
      : input.connection === 'disconnected' ? 'disconnected'
      : input.connection !== 'connected' ? 'unverified'
      : missing ? 'missing'
      : sharedWith.length > 0 ? 'shared'
      : 'sent';
    return { type, name, value, template, state, sharedWith, menu };
  });

  const going = rows.filter((r) => r.state === 'sent' || r.state === 'shared').length;
  const head: WalletHead = !enabled ? { kind: 'off' }
    : input.connection === 'disconnected' ? { kind: 'disconnected' }
    : input.connection === 'checking' ? { kind: 'checking' }
    : input.connection === 'unknown' ? { kind: 'unknown' }
    : going === 0 ? { kind: 'none' }
    : { kind: 'some', n: going, total: COUPON_TITLE_TYPES.length };
  return { enabled, auto, head, rows };
}
